/**
 * PWA update watcher — the fix for the month-stale-bundle failure.
 *
 * WHY THIS SHAPE: index.html is served no-store and the service worker
 * caches nothing, so no *navigation* can ever be stale. What goes
 * stale is the RUNNING WINDOW: an installed Android PWA resumes its
 * existing window when tapped — no navigation happens, so no header is
 * ever consulted, and a window can live on a month-old bundle for as
 * long as the OS keeps its task alive (observed twice, 2026-08).
 * The only lever that reaches a window that never navigates is code
 * already running inside it. Hence:
 *
 *   1. RESUME CHECK (load-bearing): on visibilitychange -> visible —
 *      exactly the Android resume moment that bypasses everything —
 *      fetch /version.json (no-store) and compare against the baked-in
 *      __BUILD_ID__ (both are the git sha; see vite.config.ts).
 *   2. Slow poll while visible, for the tab that never gets hidden.
 *   3. On mismatch: toast once ("update ready"), then FORCE-RELOAD
 *      ONLY WHILE HIDDEN — the swap happens when the user isn't
 *      looking, and the next resume comes up on the new build with
 *      zero visible disruption. Never reload a visible window.
 *
 * The reload lands on a no-store index.html, so it is guaranteed to
 * pick up the new bundle. PROD-only: the dev server has no
 * version.json and hot-reloads itself.
 */
import i18n from '@/lib/i18n';
import { toast } from '@/store/toast';

/** Floor between checks so rapid app-switching doesn't spam the CDN. */
const CHECK_MIN_INTERVAL_MS = 60_000;
/** Slow poll for always-visible windows (desktop tab left open). */
const VISIBLE_POLL_MS = 15 * 60_000;
/** One early check after boot: a window restored from OS freeze can
 *  miss the visibilitychange edge. Delayed off the first-paint path. */
const BOOT_CHECK_DELAY_MS = 10_000;

let lastCheckAt = 0;
let updateReady = false;
let toastShown = false;

async function fetchRemoteBuild(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const body = await res.json() as { build?: unknown };
    return typeof body.build === 'string' && body.build.length > 0 ? body.build : null;
  } catch {
    // Network drop / captive portal — silently retry on the next
    // resume or poll tick. Never surface an error for a background
    // freshness probe.
    return null;
  }
}

async function check(): Promise<void> {
  if (updateReady) return;
  const now = Date.now();
  if (now - lastCheckAt < CHECK_MIN_INTERVAL_MS) return;
  lastCheckAt = now;
  const remote = await fetchRemoteBuild();
  if (remote === null || remote === __BUILD_ID__) return;
  updateReady = true;
  if (!toastShown && document.visibilityState === 'visible') {
    toastShown = true;
    toast(i18n.t('common:appUpdate.ready'), { tone: 'accent', durationMs: 6_000 });
  }
}

/** Wire once at boot (main.tsx), beside the SW registration. */
export function initAppUpdateWatch(): void {
  if (!import.meta.env.PROD) return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void check();
    } else if (updateReady) {
      // Hidden with an update pending: swap now, invisibly. The
      // browser honors a reload initiated in the hidden transition;
      // if the OS freezes the page first, the pending flag survives
      // in memory and the reload fires on the next hide instead.
      window.location.reload();
    }
  });
  window.setInterval(() => {
    if (document.visibilityState === 'visible') void check();
  }, VISIBLE_POLL_MS);
  window.setTimeout(() => { void check(); }, BOOT_CHECK_DELAY_MS);
}
